import { createSsccGenerator, getReusableIdentifiers, hasSscc } from 'support/helpers/packingList/generateSscc';
import { canRecoverTotalFromOrdered, readQuantity } from 'support/helpers/packingList/packableQuantity';
import { lineItemRef, refIndex, transportUnitRef } from 'support/helpers/packingList/packingListRefs';
import { WebEDIRowData } from 'support/types/webediTable';

import { LineItemEntry, LineItemId, PackingListResult, PackingListTree, TransportUnitNode } from '../types';

type LineItemPlacement = { transportUnitIndex: number; quantity: number };
type EmissionUnit = { quantity: number; transportUnit: string };

// How many transport units the tree carries without an identifier yet — i.e. how many calls the
// serializer will make to the generator.
export const countUnitsNeedingIdentifier = (nodes: Array<TransportUnitNode>): number => {
  let count = 0;
  const visit = (candidates: Array<TransportUnitNode>): void => {
    for (const node of candidates) {
      if (!hasSscc(node.sscc)) {
        count += 1;
      }
      visit(node.children);
    }
  };
  visit(nodes);
  return count;
};

// How many fresh identifiers the backend must generate for this tree: the units missing one, minus
// the identifiers the client can reuse. Mirrors what createSsccGenerator hands out, so the request
// asks for exactly the number that reuse won't cover.
export const countUnitIdentifiersToGenerate = (
  transportUnits: Array<TransportUnitNode>,
  existingUnitIdentifiers: Array<string> = [],
): number => {
  const reusable = getReusableIdentifiers(collectExistingSsccs(transportUnits), existingUnitIdentifiers);
  return Math.max(0, countUnitsNeedingIdentifier(transportUnits) - reusable.length);
};

export const packingListToFlattenData = (
  packingListTree: PackingListTree,
  quantityFieldPath = 'shipped_quantity',
  existingUnitIdentifiers: Array<string> = [],
  generatedUnitIdentifiers: Array<string> = [],
): PackingListResult => {
  const transportUnitRows: Array<WebEDIRowData> = [];
  const placementsByLineItem = new Map<LineItemId, Array<LineItemPlacement>>();
  flattenTransportUnits(
    packingListTree.transportUnits,
    transportUnitRows,
    placementsByLineItem,
    existingUnitIdentifiers,
    generatedUnitIdentifiers,
  );

  const defaultUnitIndex = transportUnitRows.length;
  transportUnitRows.push(buildDefaultTransportUnitRow());

  const { topLevelLineItemEntries, childrenByParentIndex } = groupLineItemsByParent(packingListTree.lineItems);

  const lineItemRows = expandLineItemsAcrossPlacements(
    topLevelLineItemEntries,
    childrenByParentIndex,
    placementsByLineItem,
    quantityFieldPath,
    defaultUnitIndex,
  );

  return { line_items: lineItemRows, transport_units: transportUnitRows };
};

const parentRowIndex = (lineItemEntry: LineItemEntry): number | null =>
  refIndex(lineItemEntry.row._internal?.parent_line_item);

const isTopLevel = (lineItemEntry: LineItemEntry): boolean => !lineItemEntry.row._internal?.parent_line_item;

const allocateQuantity = (quantity: number, placementQuantity: number, parentTotal: number): number =>
  parentTotal > 0 ? Number(((quantity * placementQuantity) / parentTotal).toFixed(2)) : quantity;

const collectExistingSsccs = (nodes: Array<TransportUnitNode>): Array<string> => {
  const identifiers: Array<string> = [];
  const visit = (candidates: Array<TransportUnitNode>): void => {
    for (const node of candidates) {
      if (hasSscc(node.sscc)) {
        identifiers.push(String(node.sscc));
      }
      visit(node.children);
    }
  };
  visit(nodes);
  return identifiers;
};

const flattenTransportUnits = (
  transportUnits: Array<TransportUnitNode>,
  transportUnitRows: Array<WebEDIRowData>,
  placementsByLineItem: Map<LineItemId, Array<LineItemPlacement>>,
  existingUnitIdentifiers: Array<string> = [],
  generatedUnitIdentifiers: Array<string> = [],
): void => {
  // Any unit the user built without an identifier gets one assigned here, so a non-empty
  // unit_identifier is the single signal that a transport unit is "real" (see generateSscc).
  // Units that already carry an identifier keep it; the generator reuses an existing identifier
  // no longer in use (a deleted unit's number becomes available again on its own) before drawing
  // from the freshly generated backend identifiers.
  const survivors = collectExistingSsccs(transportUnits);
  const nextSscc = createSsccGenerator(survivors, existingUnitIdentifiers, generatedUnitIdentifiers);
  const walk = (nodes: Array<TransportUnitNode>, level: number, parentIndex: number | null): void => {
    for (const node of nodes) {
      const transportUnitIndex = transportUnitRows.length;
      transportUnitRows.push({
        unit_type: node.unitType,
        unit_identifier: hasSscc(node.sscc) ? node.sscc : nextSscc(),
        _internal: {
          level,
          parent_transport_unit: parentIndex === null ? '' : transportUnitRef(parentIndex),
        },
      });
      for (const content of node.contents) {
        const placements = placementsByLineItem.get(content.lineItemId) ?? [];
        placements.push({ transportUnitIndex, quantity: content.quantity });
        placementsByLineItem.set(content.lineItemId, placements);
      }
      walk(node.children, level + 1, transportUnitIndex);
    }
  };
  walk(transportUnits, 1, null);
};

const groupLineItemsByParent = (
  lineItems: Record<LineItemId, LineItemEntry>,
): {
  topLevelLineItemEntries: Array<LineItemEntry>;
  childrenByParentIndex: Map<number, Array<LineItemEntry>>;
} => {
  const orderedLineItemEntries = Object.values(lineItems).sort((a, b) => a.originalIndex - b.originalIndex);
  const childrenByParentIndex = new Map<number, Array<LineItemEntry>>();
  const topLevelLineItemEntries: Array<LineItemEntry> = [];
  for (const lineItemEntry of orderedLineItemEntries) {
    const parentIndex = parentRowIndex(lineItemEntry);
    if (isTopLevel(lineItemEntry) || parentIndex === null) {
      topLevelLineItemEntries.push(lineItemEntry);
      continue;
    }
    const siblings = childrenByParentIndex.get(parentIndex) ?? [];
    siblings.push(lineItemEntry);
    childrenByParentIndex.set(parentIndex, siblings);
  }
  return { topLevelLineItemEntries, childrenByParentIndex };
};

const collectSubtree = (
  root: LineItemEntry,
  childrenByParentIndex: Map<number, Array<LineItemEntry>>,
): Array<LineItemEntry> => {
  const subtree: Array<LineItemEntry> = [];
  const visit = (lineItemEntry: LineItemEntry): void => {
    subtree.push(lineItemEntry);
    for (const child of childrenByParentIndex.get(lineItemEntry.originalIndex) ?? []) {
      visit(child);
    }
  };
  visit(root);
  return subtree;
};

// Each duplicate of a split line re-derives its own parent_line_item refs (newIndexByOriginal is rebuilt
// per unit) because the same subtree lands at different flat indices in every placement.
const expandLineItemsAcrossPlacements = (
  topLevelLineItemEntries: Array<LineItemEntry>,
  childrenByParentIndex: Map<number, Array<LineItemEntry>>,
  placementsByLineItem: Map<LineItemId, Array<LineItemPlacement>>,
  quantityFieldPath: string,
  defaultUnitIndex: number,
): Array<WebEDIRowData> => {
  const lineItemRows: Array<WebEDIRowData> = [];
  let displayOrder = 0;

  for (const root of topLevelLineItemEntries) {
    const total = readQuantity(root.row[quantityFieldPath]);
    const placements = placementsByLineItem.get(root.id) ?? [];
    const placedQuantity = placements.reduce((sum, placement) => sum + placement.quantity, 0);
    const remainder = total - placedQuantity;

    const units: Array<EmissionUnit> = placements.map((placement) => ({
      quantity: placement.quantity,
      transportUnit: transportUnitRef(placement.transportUnitIndex),
    }));
    // Anything the user didn't place (or a partial remainder) goes to the default unit rather than
    // an empty ref: a real index keeps the transport-unit dropdown from restoring a stale value,
    // and the default unit (no SSCC) preserves the placeholder association used by status display
    // and submit-time stripping.
    //
    // It carries 0 when ordered_quantity can recover the line total, because nothing on the default
    // unit ships and a quantity here would read as shipped against a line marked not shipped
    // (CORE-11647). Where there is no ordered quantity, this row is the only remaining record of the
    // total, so the remainder is kept — losing it would shrink the line on every reopen. Those rows
    // do still read as shipped against a line marked not shipped; that is the accepted cost, since
    // zeroing is free only where ordered_quantity holds the total.
    if (remainder > 0 || units.length === 0) {
      const remainderQuantity = canRecoverTotalFromOrdered(root.row.ordered_quantity) ? 0 : Math.max(0, remainder);
      units.push({ quantity: remainderQuantity, transportUnit: transportUnitRef(defaultUnitIndex) });
    }

    const wasSplit = units.length > 1;
    const subtree = collectSubtree(root, childrenByParentIndex);

    units.forEach((unit, placementIndex) => {
      const isFirstPlacement = placementIndex === 0;
      const newIndexByOriginal = new Map<number, number>();
      for (const node of subtree) {
        const newIndex = lineItemRows.length;
        newIndexByOriginal.set(node.originalIndex, newIndex);
        const originalParentIndex = parentRowIndex(node);
        const parentLineItem =
          originalParentIndex === null ? '' : lineItemRef(newIndexByOriginal.get(originalParentIndex) ?? newIndex);
        // The whole subtree follows the parent's placement ratio, with quantities capped at two
        // decimal places so fractional results remain practical for document entry.
        const nodeQuantity =
          node === root
            ? unit.quantity
            : allocateQuantity(readQuantity(node.row[quantityFieldPath]), unit.quantity, total);
        const producedRow: WebEDIRowData = {
          ...node.row,
          [quantityFieldPath]: nodeQuantity,
          displayOrder,
          _internal: {
            ...node.row._internal,
            parent_line_item: parentLineItem,
            parent_transport_unit: unit.transportUnit,
          },
        };
        if (wasSplit) {
          clearLineIdentifier(producedRow);
        }
        // ordered_quantity is a line-level fact, not per-placement. Keep it on the first
        // placement's copies only — for the whole subtree, root and sub-items alike; later
        // placements carry the key with value 0 so consumers that sum the field across the
        // split copies don't double-count it while every copy still carries the key.
        if (!isFirstPlacement) {
          producedRow.ordered_quantity = 0;
        }
        lineItemRows.push(producedRow);
        displayOrder += 1;
      }
    });
  }

  return lineItemRows;
};

// The default transport unit that always trails the real ones: it carries no SSCC (so it stays a
// placeholder everywhere — hidden in the table, stripped at submit) and holds every unplaced or
// not-fully-shipped quantity. Emitting it unconditionally keeps the serializer branch-free; when
// nothing is unplaced it is simply an empty unit that gets stripped.
const buildDefaultTransportUnitRow = (): WebEDIRowData => ({
  unit_type: '',
  unit_identifier: '',
  _internal: { level: 1, parent_transport_unit: '' },
});

// A split copy must not repeat the original line identifier. The field name depends on the document
// type, so clear whichever one the row carries.
const LINE_IDENTIFIER_FIELDS = ['shipping_notice_line_identifier', 'instruction_response_line_identifier'];

const clearLineIdentifier = (row: WebEDIRowData): void => {
  for (const field of LINE_IDENTIFIER_FIELDS) {
    if (field in row) {
      row[field] = '';
    }
  }
};
